import { useQueryClient } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { matchPath, useLocation, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { cx, Icon } from "@/components/ui";
import { primarySite, useOverview } from "@/lib/queries";

/**
 * Switching business keeps you on the same page, except pages about one record (a site, an invoice,
 * a card): that record belongs to the business you left, so you land on its list instead.
 */
const RECORD_PAGES: [pattern: string, list: string][] = [
  ["/websites/:siteId", "/websites"],
  ["/hosting/sites/:siteId", "/hosting"],
  ["/billing/checkout/success/:invoiceId", "/billing"],
  ["/billing/payment-methods/:methodId/saved", "/billing"],
];

const menuItems = (menu: HTMLElement | null) =>
  Array.from(menu?.querySelectorAll<HTMLElement>("[role^='menuitem']") ?? []);

/** The header's business switcher: the current organization, its plan and main address. */
export function OrganizationSwitcher({ onNewBusiness }: { onNewBusiness: () => void }) {
  const { organizations, current, orgId, switchTo } = useOrganization();
  const overview = useOverview();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  // Opening moves focus into the menu, onto the current business.
  useEffect(() => {
    if (!open) return;
    const all = menuItems(menuRef.current);
    (all.find((el) => el.getAttribute("aria-checked") === "true") ?? all[0])?.focus();
  }, [open]);

  // The keys of the ARIA menu pattern: arrows, Home and End move; Escape closes; Tab leaves.
  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const all = menuItems(menuRef.current);
    const at = all.indexOf(document.activeElement as HTMLElement);
    const moves: Record<string, number> = {
      ArrowDown: at + 1,
      ArrowUp: at - 1,
      Home: 0,
      End: all.length - 1,
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      all[(move + all.length) % all.length]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  const plan = overview.data?.account.plan.name;
  const hostname = overview.data && primarySite(overview.data.recent_sites)?.hostname;
  const meta = [plan && `${plan} plan`, hostname].filter(Boolean).join(" · ");

  return (
    <div className="relative min-w-0" data-tour="org-switcher">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex max-w-full min-w-0 items-center gap-2.5 rounded-xl border border-border bg-surface py-1.5 pr-2.5 pl-1.5 text-left drop-shadow-[0px_2px_4px_rgba(112,71,46,0.06)] transition-colors hover:bg-[#fffdfc]"
      >
        <OrgMark name={current.organization.name} className="size-8 rounded-[9px] text-sm" />
        <span className="flex min-w-0 flex-col gap-px">
          <span className="truncate text-sm leading-5 font-semibold text-fg">
            {current.organization.name}
          </span>
          {meta && <span className="truncate text-xs leading-4 text-fg-subtle">{meta}</span>}
        </span>
        <Icon name="switcher-chevron" size={15} className="text-fg-subtle" />
      </button>

      {open && (
        <>
          {/* Clicking outside closes the menu; keyboard users press Escape (handled on the menu). */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            role="menu"
            tabIndex={-1}
            aria-label="Your businesses"
            onKeyDown={onMenuKeyDown}
            className="absolute top-full left-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-lg"
          >
            <p className="px-3 pt-2 pb-1 text-xs font-semibold text-fg-subtle">Your businesses</p>
            {organizations.map((o) => (
              <button
                key={o.organization.id}
                type="button"
                role="menuitemradio"
                aria-checked={o.organization.id === orgId}
                tabIndex={-1}
                onClick={() => {
                  close();
                  if (o.organization.id === orgId) return;
                  switchTo(o.organization.id);
                  void qc.invalidateQueries();
                  const list = RECORD_PAGES.find(([pattern]) => matchPath(pattern, pathname))?.[1];
                  if (list) navigate(list, { replace: true });
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-fg hover:bg-muted"
              >
                <OrgMark name={o.organization.name} className="size-7 rounded-lg text-xs" />
                <span className="min-w-0 flex-1 truncate font-medium">{o.organization.name}</span>
                {o.organization.id === orgId && <Check className="size-4 text-brand-500" />}
              </button>
            ))}
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                // Back on the switcher first, so the new-business dialog returns focus there when it closes.
                close();
                onNewBusiness();
              }}
              className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2 text-left text-sm font-medium text-fg hover:bg-muted"
            >
              <span className="flex size-7 items-center justify-center rounded-lg border border-dashed border-border text-fg-subtle">
                <Plus className="size-4" />
              </span>
              New business
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** A business has no logo yet: its initial on the design's cream tile. */
function OrgMark({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cx(
        "flex shrink-0 items-center justify-center bg-brand-50 font-bold text-brand-500 uppercase",
        className,
      )}
      aria-hidden
    >
      {name.trim()[0] ?? "?"}
    </span>
  );
}
